import { FormFieldDto } from '../../general/dtos';
import { markdownHtml, markdownText } from '../../general/markdown';

export function addMarkdownFieldHelp(container: HTMLElement, input: HTMLElement, field: FormFieldDto, doc: Document) {
    if (!field.tooltip?.trim()) return;
    const id = `${field.key}-help`;
    const accessible = doc.createElement('span');
    accessible.id = id;
    accessible.hidden = true;
    accessible.textContent = markdownText(field.tooltip);
    input.setAttribute('aria-describedby', [input.getAttribute('aria-describedby'), id].filter(Boolean).join(' '));
    input.setAttribute('data-markdown-help', markdownHtml(field.tooltip));
    const button = doc.createElement('button');
    button.type = 'button';
    button.className = 'field-help-button';
    button.textContent = '?';
    button.setAttribute('aria-label', `Help for ${markdownText(field.label) || 'this field'}`);
    button.setAttribute('aria-describedby', id);
    button.setAttribute('data-markdown-help', markdownHtml(field.tooltip));
    container.append(button, accessible);
}

export const MARKDOWN_FIELD_HELP_STYLES = `
.field-help-button { font:inherit; font-size:.8em; line-height:1.25; min-width:1.5em; padding:.05em .3em; margin:0 .2em; border:1px solid currentColor; border-radius:50%; background:transparent; color:inherit; cursor:pointer; flex-shrink:0; }
.field-markdown-tooltip { position:fixed; z-index:2147483647; box-sizing:border-box; width:max-content; max-width:calc(100vw - 2rem); max-height:min(24rem,calc(100vh - 2rem)); overflow:auto; overscroll-behavior:contain; padding:.75rem 1rem; border:1px solid var(--surface-border,#aaa); border-radius:.5rem; background:var(--surface-overlay,#fff); color:var(--text-color,#222); box-shadow:0 .25rem 1rem #0003; font-size:.9rem; }
.field-markdown-tooltip[hidden] { display:none; }
.field-help-close { display:block; margin:0 0 .5rem auto; border:0; padding:.1rem .4rem; font:inherit; color:inherit; background:transparent; cursor:pointer; }
`;

/** Runs in the lesson iframe. Focus/hover and an explicit help button cover mouse, keyboard and touch. */
export const MARKDOWN_FIELD_HELP_SCRIPT = String.raw`
(function () {
    const tooltip = document.createElement('div');
    tooltip.className = 'field-markdown-tooltip';
    tooltip.setAttribute('role', 'tooltip');
    tooltip.hidden = true;
    document.body.appendChild(tooltip);
    let timer, active = null, pinned = false;
    function hide() { clearTimeout(timer); tooltip.hidden = true; active = null; pinned = false; }
    function later() { clearTimeout(timer); if (!pinned) timer = setTimeout(hide, 200); }
    function show(target, pin) {
        clearTimeout(timer);
        active = target; pinned = !!pin;
        const close = document.createElement('button');
        close.type = 'button'; close.className = 'field-help-close'; close.textContent = 'Close';
        close.setAttribute('aria-label', 'Close field help');
        close.addEventListener('click', hide);
        const content = document.createElement('div');
        content.className = 'markdown-content';
        content.innerHTML = target.getAttribute('data-markdown-help');
        tooltip.replaceChildren(close, content);
        tooltip.hidden = false;
        tooltip.style.width = Math.min(512, innerWidth - 32) + 'px';
        const r = target.getBoundingClientRect();
        const height = tooltip.getBoundingClientRect().height;
        tooltip.style.left = Math.max(16, Math.min(r.left, innerWidth - tooltip.offsetWidth - 16)) + 'px';
        const below = innerHeight - r.bottom - 24;
        const top = below >= height ? r.bottom + 8 : r.top - height - 8;
        tooltip.style.top = Math.max(16, Math.min(top, innerHeight - height - 16)) + 'px';
    }
    document.querySelectorAll('[data-markdown-help]').forEach(target => {
        target.addEventListener('mouseenter', () => { if (!pinned) show(target, false); });
        target.addEventListener('mouseleave', later);
        target.addEventListener('focusin', () => { if (!pinned) show(target, false); });
        target.addEventListener('focusout', later);
        if (target.classList.contains('field-help-button')) target.addEventListener('click', () => {
            if (active === target && pinned) hide(); else show(target, true);
        });
    });
    tooltip.addEventListener('mouseenter', () => clearTimeout(timer));
    tooltip.addEventListener('mouseleave', later);
    tooltip.addEventListener('focusin', () => clearTimeout(timer));
    tooltip.addEventListener('focusout', later);
    document.addEventListener('keydown', event => { if (event.key === 'Escape') hide(); });
    document.addEventListener('pointerdown', event => {
        if (active && !active.contains(event.target) && !tooltip.contains(event.target)) hide();
    });
    document.addEventListener('scroll', event => { if (!tooltip.contains(event.target)) hide(); }, true);
    window.addEventListener('resize', hide);
})();
`;
